/**
 * Currency reference table. Seeds the `currencies` table on the API side and
 * drives the picker on the web side, so the two can never drift apart.
 */

export interface CurrencyMeta {
  code: string;
  nameZh: string;
  nameEn: string;
  symbol: string;
  /**
   * Regional-indicator pair. Renders as a flag on iOS, macOS and Android;
   * Windows has no flag glyphs and falls back to the two letters ("AU"), which
   * is still the right information, just less decorative. Deliberately not
   * swapped for images - two characters beat 30 sprite requests.
   */
  flag: string;
  /** Minor-unit digits. JPY has none; most others have two. */
  decimals: number;
}

export const CURRENCIES: readonly CurrencyMeta[] = [
  { code: 'CNY', nameZh: '人民币', nameEn: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳', decimals: 2 },
  { code: 'AUD', nameZh: '澳大利亚元', nameEn: 'Australian Dollar', symbol: 'A$', flag: '🇦🇺', decimals: 2 },
  { code: 'USD', nameZh: '美元', nameEn: 'US Dollar', symbol: '$', flag: '🇺🇸', decimals: 2 },
  { code: 'HKD', nameZh: '港元', nameEn: 'Hong Kong Dollar', symbol: 'HK$', flag: '🇭🇰', decimals: 2 },
  { code: 'JPY', nameZh: '日元', nameEn: 'Japanese Yen', symbol: '¥', flag: '🇯🇵', decimals: 0 },
  { code: 'EUR', nameZh: '欧元', nameEn: 'Euro', symbol: '€', flag: '🇪🇺', decimals: 2 },
  { code: 'GBP', nameZh: '英镑', nameEn: 'British Pound', symbol: '£', flag: '🇬🇧', decimals: 2 },
  { code: 'SGD', nameZh: '新加坡元', nameEn: 'Singapore Dollar', symbol: 'S$', flag: '🇸🇬', decimals: 2 },
  { code: 'NZD', nameZh: '新西兰元', nameEn: 'New Zealand Dollar', symbol: 'NZ$', flag: '🇳🇿', decimals: 2 },
  { code: 'CAD', nameZh: '加拿大元', nameEn: 'Canadian Dollar', symbol: 'C$', flag: '🇨🇦', decimals: 2 },
  { code: 'CHF', nameZh: '瑞士法郎', nameEn: 'Swiss Franc', symbol: 'Fr', flag: '🇨🇭', decimals: 2 },
  { code: 'KRW', nameZh: '韩元', nameEn: 'South Korean Won', symbol: '₩', flag: '🇰🇷', decimals: 0 },
  { code: 'TWD', nameZh: '新台币', nameEn: 'New Taiwan Dollar', symbol: 'NT$', flag: '🇹🇼', decimals: 2 },
  { code: 'THB', nameZh: '泰铢', nameEn: 'Thai Baht', symbol: '฿', flag: '🇹🇭', decimals: 2 },
  { code: 'MYR', nameZh: '马来西亚林吉特', nameEn: 'Malaysian Ringgit', symbol: 'RM', flag: '🇲🇾', decimals: 2 },
  { code: 'PHP', nameZh: '菲律宾比索', nameEn: 'Philippine Peso', symbol: '₱', flag: '🇵🇭', decimals: 2 },
  { code: 'IDR', nameZh: '印尼盾', nameEn: 'Indonesian Rupiah', symbol: 'Rp', flag: '🇮🇩', decimals: 0 },
  { code: 'INR', nameZh: '印度卢比', nameEn: 'Indian Rupee', symbol: '₹', flag: '🇮🇳', decimals: 2 },
  { code: 'VND', nameZh: '越南盾', nameEn: 'Vietnamese Dong', symbol: '₫', flag: '🇻🇳', decimals: 0 },
  { code: 'AED', nameZh: '阿联酋迪拉姆', nameEn: 'UAE Dirham', symbol: 'د.إ', flag: '🇦🇪', decimals: 2 },
  { code: 'SEK', nameZh: '瑞典克朗', nameEn: 'Swedish Krona', symbol: 'kr', flag: '🇸🇪', decimals: 2 },
  { code: 'NOK', nameZh: '挪威克朗', nameEn: 'Norwegian Krone', symbol: 'kr', flag: '🇳🇴', decimals: 2 },
  { code: 'DKK', nameZh: '丹麦克朗', nameEn: 'Danish Krone', symbol: 'kr', flag: '🇩🇰', decimals: 2 },
  { code: 'PLN', nameZh: '波兰兹罗提', nameEn: 'Polish Zloty', symbol: 'zł', flag: '🇵🇱', decimals: 2 },
  { code: 'CZK', nameZh: '捷克克朗', nameEn: 'Czech Koruna', symbol: 'Kč', flag: '🇨🇿', decimals: 2 },
  { code: 'HUF', nameZh: '匈牙利福林', nameEn: 'Hungarian Forint', symbol: 'Ft', flag: '🇭🇺', decimals: 2 },
  { code: 'TRY', nameZh: '土耳其里拉', nameEn: 'Turkish Lira', symbol: '₺', flag: '🇹🇷', decimals: 2 },
  { code: 'ZAR', nameZh: '南非兰特', nameEn: 'South African Rand', symbol: 'R', flag: '🇿🇦', decimals: 2 },
  { code: 'BRL', nameZh: '巴西雷亚尔', nameEn: 'Brazilian Real', symbol: 'R$', flag: '🇧🇷', decimals: 2 },
  { code: 'MXN', nameZh: '墨西哥比索', nameEn: 'Mexican Peso', symbol: 'MX$', flag: '🇲🇽', decimals: 2 },
  { code: 'ILS', nameZh: '以色列新谢克尔', nameEn: 'Israeli New Shekel', symbol: '₪', flag: '🇮🇱', decimals: 2 },
  { code: 'RUB', nameZh: '俄罗斯卢布', nameEn: 'Russian Ruble', symbol: '₽', flag: '🇷🇺', decimals: 2 },
];

const BY_CODE = new Map(CURRENCIES.map((c) => [c.code, c]));

export const currencyCodes: readonly string[] = CURRENCIES.map((c) => c.code);

export const isCurrencyCode = (code: string): boolean => BY_CODE.has(code);

export const getCurrency = (code: string): CurrencyMeta | undefined => BY_CODE.get(code);

/**
 * Display precision for a rate quoted as base-per-quote.
 *
 * A JPY rate lives around 0.0489, so two decimals would round every distinct
 * price to the same number; a rate above 1 rarely needs more than four.
 */
export function rateDecimals(rate: number): number {
  const abs = Math.abs(rate);
  if (abs === 0) return 4;
  if (abs >= 100) return 3;
  if (abs >= 1) return 4;
  if (abs >= 0.01) return 6;
  return 8;
}
